"use client";

import Link from "next/link";
import { useHydrated } from "@/hooks/use-hydrated";
import type { RecitalSummary } from "@/lib/api/recitals-api";
import { recitalPath } from "@/lib/live/recital-routes";
import { sharePieceLine } from "@/lib/live/recital-share";
import { absoluteLabel, relativeLabel } from "@/lib/live/recital-time";
import { LockIcon } from "./live-buttons";

/**
 * Un récital, tel qu'on le voit sur `/live` avant d'entrer dans sa salle.
 *
 * # L'affiche, dans l'ordre où on la lit
 *
 * Quand, puis **quoi**, puis **qui**, puis combien de places. C'est l'ordre
 * inverse de la carte de séance, où le nom passe avant le morceau parce qu'on
 * entre chez quelqu'un. Ici on n'entre nulle part encore : on lit une
 * affiche, et une affiche commence par la date et le titre.
 *
 * # Un lien et non un bouton
 *
 * La carte de séance est un bouton parce qu'entrer, c'est un `push` vers le
 * studio. La salle d'attente d'un récital est une vraie page avec sa propre
 * adresse : on peut l'ouvrir dans un autre onglet, la copier, l'envoyer. Un
 * `<a>` rend tout ça gratuitement.
 *
 * # Terminé ou annulé, grisé mais là
 *
 * Dans « Mes récitals », une carte peut parler d'un récital fini. Elle reste
 * un lien — sa salle dit « Terminé. » et qui avait réservé — mais passe en
 * retrait, et son étiquette remplace la date relative : « il y a 3 jours »
 * d'un récital annulé ne dirait pas qu'il n'a pas eu lieu.
 *
 * # Les deux dates, après l'hydratation seulement
 *
 * « dans 2 h 30 » dit s'il faut attendre ou revenir ; « samedi 20 septembre ·
 * 19:00 » se note. Toutes deux sont en heure locale, que le serveur ne
 * connaît pas : rendues côté serveur elles arriveraient en UTC, et React
 * jetterait l'arbre à l'hydratation pour un texte qui diffère. Elles ne sont
 * donc écrites qu'une fois la page hydratée (`useHydrated`), un blanc de la
 * même hauteur tenant la place jusque-là — le temps d'un battement.
 */
export function RecitalCard({ recital }: { recital: RecitalSummary }) {
  const who = recital.performer.nickname ?? "Quelqu'un";
  const live = recital.status === "live";
  const over = recital.status === "ended" || recital.status === "cancelled";
  const hydrated = useHydrated();
  const when = hydrated ? whenLabels(recital.scheduledAt) : null;

  return (
    <Link
      href={recitalPath(recital.id)}
      aria-label={`${recital.title}, ${recital.recordingOnly ? "enregistrement" : "récital"} de ${who}`}
      className={`relative flex w-full flex-col gap-3.5 p-4 text-left rounded-xl border border-border-subtle bg-surface-card ${over ? "opacity-55" : ""} cursor-pointer transition-all duration-150 hover:-translate-y-px hover:border-accent/45 hover:bg-surface-card-hover hover:shadow-[var(--shadow-card-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent motion-reduce:transition-none motion-reduce:hover:translate-y-0`}
    >
      <div className="flex items-center justify-between gap-2">
        {live ? (
          <span className="inline-flex items-center gap-1.5 font-mono text-[9px] font-semibold uppercase tracking-[0.16em] text-text-secondary">
            <span
              aria-hidden="true"
              className="live-onair-dot block w-1.5 h-1.5 rounded-full bg-err"
            />
            en cours
          </span>
        ) : over ? (
          <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.16em] text-text-muted">
            {recital.status === "ended" ? "terminé" : "annulé"}
          </span>
        ) : (
          <span className="font-mono text-[10px] tracking-[0.04em] text-text-secondary tabular-nums">
            {when?.relative ?? "\u00a0"}
          </span>
        )}

        <span className="inline-flex items-center gap-1.5">
          {recital.recordingOnly ? <NoAudienceBadge /> : recital.visibility === "private" && <PrivateBadge />}

          {recital.registered && (
            <span className="font-mono text-[9px] uppercase tracking-[0.12em] px-1.5 py-[3px] rounded-[5px] text-accent-bright bg-accent-subtle">
              inscrit·e
            </span>
          )}
        </span>
      </div>

      <div className="min-w-0">
        <span className="block text-[15px] font-medium leading-tight text-text-primary truncate">
          {recital.title}
        </span>
        <span className="block mt-1 text-[12px] text-text-soft truncate">
          {when?.absolute ?? "\u00a0"}
        </span>
      </div>

      <div className="flex items-center gap-2.5 min-w-0">
        <span
          aria-hidden="true"
          className="w-[30px] h-[30px] shrink-0 grid place-items-center rounded-full text-[12px] font-semibold bg-accent-subtle text-accent-bright"
        >
          {initial(who)}
        </span>
        <span className="min-w-0">
          <span className="block text-[13px] text-text-primary truncate">{who}</span>
          {recital.piece !== null && (
            <span className="block text-[12px] truncate text-text-soft">{pieceLine(recital)}</span>
          )}
        </span>
      </div>

      <div className="flex items-center justify-between gap-3 pt-3 border-t border-border-subtle">
        {recital.recordingOnly ? (
          <span className="text-[12px] text-text-soft">Pour le replay</span>
        ) : (
          <SeatsGauge registrations={recital.registrations} seats={recital.seats} />
        )}
        {live && !recital.recordingOnly && (
          <span className="shrink-0 text-[12px] text-text-secondary tabular-nums">
            {recital.listeners} à l&apos;écoute
          </span>
        )}
      </div>
    </Link>
  );
}

/**
 * « Sans public » : un récital qui se joue pour être enregistré. Le point rouge
 * est celui de l'enregistrement, qu'on retrouve dans le studio.
 */
export function NoAudienceBadge() {
  return (
    <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.12em] px-1.5 py-[3px] rounded-[5px] text-text-secondary bg-white/[0.06]">
      <span aria-hidden="true" className="size-1.5 rounded-full bg-red-400" />
      sans public
    </span>
  );
}

/**
 * « Privé », avec son cadenas.
 *
 * Une carte de récital privé ne se voit que là où l'on a le droit de la voir —
 * « Mes récitals » — et c'est justement là qu'il faut le dire : sans
 * l'étiquette, rien ne rappellerait que ce récital n'est pas sur l'affiche,
 * et qu'il faut donner son lien ou son code pour y faire venir quelqu'un.
 * Exportée pour la salle d'attente, qui porte la même.
 */
export function PrivateBadge() {
  return (
    <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.12em] px-1.5 py-[3px] rounded-[5px] text-text-secondary bg-white/[0.06]">
      <LockIcon size={9} />
      privé
    </span>
  );
}

/**
 * « 12 / 30 places » et un trait qui se remplit ; « places illimitées » quand
 * rien ne borne.
 *
 * Le trait n'est pas décoratif : à 28 / 30 il dit « dépêche-toi » avant qu'on
 * ait lu les chiffres, et c'est la seule information de l'affiche qui presse.
 * Complet, il passe en gris — la couleur d'accent dit « il reste de la
 * place », et un trait violet plein dirait le contraire de ce qu'il montre.
 */
export function SeatsGauge({
  registrations,
  seats,
  wide = false,
}: {
  registrations: number;
  seats: number | null;
  /** Prend toute la largeur donnée (salle d'attente) au lieu de se partager une ligne. */
  wide?: boolean;
}) {
  if (seats === null) {
    return (
      <span className="text-[12px] text-text-secondary tabular-nums">
        {registrations === 0
          ? "places illimitées"
          : `${registrations} inscrit${registrations > 1 ? "·es" : "·e"} · places illimitées`}
      </span>
    );
  }

  const full = registrations >= seats;
  const ratio = Math.min(1, registrations / seats);

  return (
    <span className={`flex min-w-0 flex-col gap-1.5 ${wide ? "w-full" : "flex-1"}`}>
      <span className="text-[12px] text-text-secondary tabular-nums">
        {full ? `complet · ${seats} places` : `${registrations} / ${seats} places`}
      </span>
      <span
        aria-hidden="true"
        className="block h-[3px] w-full rounded-full bg-white/[0.07] overflow-hidden"
      >
        <span
          className={`block h-full rounded-full transition-[width] duration-500 ${
            full ? "bg-text-muted" : "bg-accent"
          }`}
          style={{ width: `${ratio * 100}%` }}
        />
      </span>
    </span>
  );
}

/** « Nocturne op. 9 n° 2 · Chopin », ou rien quand aucun morceau n'est annoncé. */
export function pieceLine(recital: RecitalSummary): string | null {
  return sharePieceLine(recital);
}

/** La première lettre du nom, en capitale. Jamais vide. */
export function initial(name: string): string {
  const first = [...name.trim()][0];

  return (first ?? "?").toUpperCase();
}

/**
 * Les deux dates de l'affiche, lues sur l'horloge du rendu. Hors du corps du
 * composant parce que `Date.now()` y serait un appel impur au rendu ; ici il
 * est assumé — l'appel n'a lieu qu'après l'hydratation, et la sonde de trente
 * secondes re-rend la carte avant que la valeur ne vieillisse.
 */
function whenLabels(scheduledAt: string): { relative: string; absolute: string } {
  const now = Date.now();

  return {
    relative: relativeLabel(now, scheduledAt),
    absolute: absoluteLabel(scheduledAt, now),
  };
}
